// Geradores de HTML pro Recibo (apos baixa em lote) e Extrato de Conferencia (impressao).
// Inclui helper pra converter o HTML em JPG base64 (envio WhatsApp como imagem,
// igual ControleFC antigo — formato que cliente abre direto na thumbnail).
import { formatDataSP, rotuloDaNota } from '@fiodecobre/shared';
import { carregarImagemComoDataUrl, htmlParaJpg } from '../../../utils/html-para-jpg';

const fmt = (v: number | string | null | undefined): string =>
  Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ⚠️ Delega ao `formatDataSP` do shared, que trata "YYYY-MM-DD" como dia LOCAL. O `new Date(d)`
// que morava aqui lia data pura como meia-noite UTC e imprimia o DIA ANTERIOR: medido em
// 01/09/2026 contra o banco, os 16 vencimentos do recibo da ILUMINAR sairam um dia a menos
// (venc. 14/08 no papel como 13/08). A emissao escapava por ser `timestamptz`, com hora junto.
const fmtData = (d: string | null | undefined): string => (d ? formatDataSP(d) : '-');

export interface TituloRecibo {
  orcamento_numero: string | null;
  /** A nota do pedido ([DA-632]). O TIPO viaja junto porque o mesmo NUMERO existe nas duas
   *  series — o rotulo sai pela peca unica `rotuloDaNota` ([DA-630]), a mesma frase que o
   *  boleto do AppCel entrega ao cliente. `null` = sem nota autorizada (legado, consignacao). */
  nota_tipo?: 'nfe' | 'nfce' | null;
  nota_numero?: number | null;
  data_emissao: string | null;
  data_vencimento: string;
  valor_original: number;
  valor_pago: number;
  saldo: number;
  /** A forma com que ESTE titulo foi pago ([DA-654]). Ela desceu do cabecalho pra linha porque
   *  um mesmo pagamento pode ter mais de uma: o EDS quitou R$ 406,00 em Pix + Dinheiro e o
   *  recibo, com a forma so no topo, tinha como dizer no maximo metade da verdade. */
  forma_pagamento?: string | null;
  /** Este titulo e' o SALDO de outro (nasceu do que faltou numa baixa parcial). So marca a
   *  linha — o cliente conhece o pedido, nao o numero interno do titulo. */
  eh_saldo?: boolean;
  /** Id do titulo. Nao sai impresso: serve pra cruzar com `saldos_gerados` e nao anunciar como
   *  pendente um saldo que ja foi quitado dentro do proprio recibo ([DA-654]). */
  conta_receber_id?: number | null;
}

export interface ReciboParams {
  cliente: string;
  cliente_cpf_cnpj?: string | null;
  /** Fica no cabecalho SO quando o pagamento inteiro saiu numa forma so. Com mais de uma, o
   *  cabecalho cala e quem responde e' a coluna Forma + a linha de totais ([DA-654]) — cabecalho
   *  dizendo "Pix" num papel que tem Dinheiro dentro afirma o que nao aconteceu. */
  formaPagamento: string;
  operador: string;
  data: string;
  /** So quando ela e' MEDIDA (recibo emitido no instante da baixa). Na reimpressao nao ha hora
   *  gravada no titulo — `data_pagamento` e' `date` —, entao a linha sai so com a data. */
  hora?: string;
  titulos: TituloRecibo[];
  saldos_gerados?: Array<{ id: number; valor: number; data_vencimento: string }>;
  /** O que o cliente PAGOU — soma da coluna Pago (que ja inclui o acrescimo do cartao, 0321). */
  total_recebido: number;
  observacoes?: string | null;
}

export function gerarReciboHTML(p: ReciboParams): string {
  // As formas que de fato aparecem no papel. Uma so → ela fica no cabecalho, como sempre foi;
  // duas ou mais → o cabecalho cala e cada linha responde por si.
  const formasNoRecibo = [...new Set(p.titulos.map((t) => t.forma_pagamento).filter((f): f is string => !!f))];
  const temColunaForma = formasNoRecibo.length > 1;

  // O saldo desta linha foi quitado DENTRO deste mesmo recibo? Entao ele nao e' divida: a linha
  // de baixo, marcada como `(saldo)`, e' justamente ele sendo pago. Sem isto o papel imprime
  // "Saldo R$ 73,00" pra um cliente que nao deve mais nada — a mesma confusao que a lista fazia
  // ao dizer "R$ 139,00 · Pago" ([DA-654]).
  const saldoQuitadoAqui = (t: TituloRecibo): boolean =>
    t.saldo > 0 && p.titulos.some((o) => o.eh_saldo
      && o.orcamento_numero === t.orcamento_numero
      && Math.round(Number(o.valor_original) * 100) === Math.round(t.saldo * 100));

  const linhasTitulos = p.titulos
    .map(
      (t) => `
      <tr>
        <td style="padding:2px 4px">Orc. ${t.orcamento_numero || '-'}${t.eh_saldo ? ' <span style="color:#666">(saldo)</span>' : ''}</td>
        <td style="padding:2px 4px">${t.nota_tipo && t.nota_numero ? rotuloDaNota({ tipo: t.nota_tipo, numero: t.nota_numero }) : '—'}</td>
        <td style="padding:2px 4px">${fmtData(t.data_emissao)}</td>
        <td style="padding:2px 4px">${fmtData(t.data_vencimento)}</td>
        <td style="padding:2px 4px;text-align:right">R$ ${fmt(t.valor_original)}</td>
        <td style="padding:2px 4px;text-align:right">R$ ${fmt(t.valor_pago)}</td>
        ${temColunaForma ? `<td style="padding:2px 4px">${t.forma_pagamento || '—'}</td>` : ''}
        <td style="padding:2px 4px;text-align:right">${
          t.saldo <= 0 ? '—'
          : saldoQuitadoAqui(t) ? `<span style="color:#666">R$ ${fmt(t.saldo)} (quitado abaixo)</span>`
          : `R$ ${fmt(t.saldo)}`
        }</td>
      </tr>`,
    )
    .join('');

  // Quanto entrou POR FORMA — a resposta pra "e o dinheiro, cadê?" quando o papel tem duas.
  const totaisPorForma = temColunaForma
    ? `<div style="margin-top:6px;font-size:12px;display:flex;gap:18px;justify-content:flex-end;color:#444">
        ${formasNoRecibo
          .map((f) => {
            const soma = p.titulos.filter((t) => t.forma_pagamento === f).reduce((s, t) => s + Number(t.valor_pago || 0), 0);
            return `<span>${f}: <strong>R$ ${fmt(soma)}</strong></span>`;
          })
          .join('')}
      </div>`
    : '';

  // ⚠️ Saldo que foi QUITADO dentro deste mesmo recibo nao e' pendencia — anuncia-lo como
  // "saldo pendente gerado" duas linhas abaixo de onde ele aparece pago e' exatamente a confusao
  // que o Ruan relatou. Quem esta na tabela ja foi cobrado; sai do bloco.
  const idsNoRecibo = new Set(p.titulos.map((t) => t.conta_receber_id).filter((v): v is number => v != null));
  const saldosAindaEmAberto = (p.saldos_gerados ?? []).filter((s) => !idsNoRecibo.has(s.id));
  const saldosBloco =
    saldosAindaEmAberto.length > 0
      ? `<div style="margin-top:8px;padding:6px;border:1px dashed #999;font-size:12px">
          <strong>Saldos pendentes gerados:</strong>
          ${saldosAindaEmAberto.map((s) => `<div>Titulo #${s.id} — R$ ${fmt(s.valor)} venc. ${fmtData(s.data_vencimento)}</div>`).join('')}
        </div>`
      : '';

  const obsBloco = p.observacoes ? `<div style="margin-top:8px;font-size:12px"><strong>Observacoes:</strong> ${p.observacoes}</div>` : '';

  return `<html><head><title>Recibo - ${p.cliente}</title>
<style>
  body { font-family: Arial, sans-serif; margin: 16px; color: #000; }
  h1 { font-size: 18px; margin: 0 0 4px 0; text-align: center; }
  .sub { text-align: center; font-size: 12px; color: #666; margin-bottom: 12px; }
  .info { border-top: 1px solid #000; border-bottom: 1px solid #000; padding: 6px 0; margin: 8px 0; font-size: 13px; }
  table { width: 100%; border-collapse: collapse; font-size: 12px; margin-top: 6px; }
  th { background: #f0f0f0; padding: 4px; text-align: left; border-bottom: 1px solid #ccc; }
  td { border-bottom: 1px dotted #ccc; }
  .total { border-top: 2px solid #000; padding-top: 6px; margin-top: 6px; font-weight: bold; font-size: 14px; display: flex; justify-content: space-between; }
  @media print { body { margin: 8px; } }
</style></head><body>
  <h1>RECIBO DE PAGAMENTO</h1>
  <div class="sub">Fio de Cobre - Materiais Eletricos · CNPJ: 49.762.154/0001-00</div>
  <div class="info">
    <div><strong>Cliente:</strong> ${p.cliente}${p.cliente_cpf_cnpj ? ` · CPF/CNPJ: ${p.cliente_cpf_cnpj}` : ''}</div>
    ${temColunaForma ? '' : `<div><strong>Forma de pagamento:</strong> ${p.formaPagamento}</div>`}
    <div><strong>Operador:</strong> ${p.operador}</div>
    <div><strong>Data:</strong> ${p.data}${p.hora ? ` ${p.hora}` : ''}</div>
  </div>
  <table>
    <thead><tr>
      <th>Orcamento</th><th>Nota</th><th>Emissao</th><th>Vencimento</th>
      <th style="text-align:right">Original</th><th style="text-align:right">Pago</th>
      ${temColunaForma ? '<th>Forma</th>' : ''}
      <th style="text-align:right">Saldo</th>
    </tr></thead>
    <tbody>${linhasTitulos}</tbody>
  </table>
  ${totaisPorForma}
  <div class="total"><span>TOTAL RECEBIDO</span><span>R$ ${fmt(p.total_recebido)}</span></div>
  ${saldosBloco}
  ${obsBloco}
  <div style="text-align:center;margin-top:24px;font-size:11px;color:#999">Fio de Cobre by ControleFC</div>
</body></html>`;
}

// JPG (base64 sem prefixo) do recibo de pagamento pra envio via WhatsApp como imagem.
// O recibo cabe sempre numa pagina, entao nao pagina. A esteira e a mesma peca compartilhada do
// extrato — ate a DA-417 este arquivo tinha DUAS copias identicas dela (esta e a do extrato).
export async function gerarReciboJpg(p: ReciboParams): Promise<string> {
  return htmlParaJpg(gerarReciboHTML(p));
}

export interface ContaParaExtrato {
  id: number;
  orcamento_numero: string | null;
  data_emissao: string | null;
  valor: number | string;
}

export interface ItemExtrato {
  codigo: string;
  produto: string;
  foto_url: string | null;
  unidade: string | null;
  qtd: number;
  preco: number;
  subtotal: number;
}

export interface ExtratoData {
  [crId: number]: { comprador: string | null; itens: ItemExtrato[]; nf_numero?: number; nf_tipo?: string };
}

export interface ExtratoParams {
  cliente: string;
  cliente_cpf_cnpj?: string | null;
  contas: ContaParaExtrato[];
  extratoData: ExtratoData;
  totalSelecionado: number;
  // Info de paginacao quando o extrato e dividido em multiplas paginas
  pagina?: { atual: number; total: number; mostrarTotal: boolean };
}

// Gera HTML imprimivel multi-pagina do extrato. Cada bloco de orcamento e enumerado.
// Usa estilos INLINE (sem CSS classes) — assim funciona tanto em <iframe> de print
// quanto em <div> capturado pelo html2canvas (que ignora <style> em alguns casos).
export function gerarExtratoHTML(p: ExtratoParams): string {
  const vendasOrdenadas = [...p.contas].sort((a, b) => (a.data_emissao || '').localeCompare(b.data_emissao || ''));

  let html = `<html><head><title>Extrato - ${p.cliente}</title>
<style>@media print { body { margin: 8px; } .bloco { page-break-inside: avoid; } }</style>
</head><body style="font-family:Arial,sans-serif;margin:16px;color:#000;">
  <div style="text-align:center;margin-bottom:8px">
    <div style="font-size:20px;font-weight:bold">EXTRATO DE TITULOS EM ABERTO</div>
    <div style="font-size:13px;color:#666">Fio de Cobre - Materiais Eletricos</div>
    <div style="font-size:13px;color:#666">CNPJ: 49.762.154/0001-00</div>
  </div>
  <div style="border-top:1px solid #000;border-bottom:1px solid #000;padding:6px 0;margin:10px 0;font-size:14px">
    <div><strong>Cliente:</strong> ${p.cliente}</div>
    ${p.cliente_cpf_cnpj ? `<div><strong>CPF/CNPJ:</strong> ${p.cliente_cpf_cnpj}</div>` : ''}
    <div><strong>Data:</strong> ${new Date().toLocaleDateString('pt-BR')}</div>
    ${p.pagina && p.pagina.total > 1 ? `<div><strong>Pagina:</strong> ${p.pagina.atual} de ${p.pagina.total}</div>` : ''}
  </div>`;

  vendasOrdenadas.forEach((c, idx) => {
    const info = p.extratoData[c.id] || { comprador: null, itens: [] };
    const itens = info.itens || [];
    html += `<div class="bloco" style="margin-bottom:12px">
      <div style="background:#f0f0f0;padding:4px 6px;font-weight:bold;font-size:14px;border-bottom:1px solid #ccc">
        ${idx + 1}. ${info.comprador ? `Comprador: ${info.comprador} — ` : ''}Orcamento ${c.orcamento_numero || '-'}${info.nf_numero ? ` / NF ${info.nf_numero}` : ''} — Emissao: ${fmtData(c.data_emissao)} — R$ ${fmt(Number(c.valor))}
      </div>`;
    if (itens.length > 0) {
      html += `<table style="width:100%;border-collapse:collapse;font-size:12px;table-layout:fixed">
        <colgroup><col style="width:32px"/><col style="width:60px"/><col/><col style="width:80px"/><col style="width:80px"/><col style="width:80px"/></colgroup>
        <thead><tr>
          <th style="padding:2px 4px;text-align:left;border-bottom:1px solid #ccc;font-weight:bold"></th>
          <th style="padding:2px 4px;text-align:left;border-bottom:1px solid #ccc;font-weight:bold">Cod</th>
          <th style="padding:2px 4px;text-align:left;border-bottom:1px solid #ccc;font-weight:bold">Produto</th>
          <th style="padding:2px 4px;text-align:right;border-bottom:1px solid #ccc;font-weight:bold">Qtd</th>
          <th style="padding:2px 4px;text-align:right;border-bottom:1px solid #ccc;font-weight:bold">Unit.</th>
          <th style="padding:2px 4px;text-align:right;border-bottom:1px solid #ccc;font-weight:bold">Subtotal</th>
        </tr></thead><tbody>`;
      for (const it of itens) {
        html += `<tr>
          <td style="padding:2px 4px;border-bottom:1px dotted #ddd;text-align:center;width:32px">${it.foto_url ? `<img src="${it.foto_url}" style="width:24px;height:24px;object-fit:contain;vertical-align:middle"/>` : ''}</td>
          <td style="padding:2px 4px;border-bottom:1px dotted #ddd;font-size:11px;color:#666">${it.codigo || ''}</td>
          <td style="padding:2px 4px;border-bottom:1px dotted #ddd;overflow:hidden">${it.produto}</td>
          <td style="padding:2px 4px;border-bottom:1px dotted #ddd;text-align:right;white-space:nowrap">${it.qtd % 1 === 0 ? it.qtd : it.qtd.toFixed(3)} ${it.unidade || ''}</td>
          <td style="padding:2px 4px;border-bottom:1px dotted #ddd;text-align:right;white-space:nowrap">R$ ${fmt(it.preco)}</td>
          <td style="padding:2px 4px;border-bottom:1px dotted #ddd;text-align:right;white-space:nowrap">R$ ${fmt(it.subtotal)}</td>
        </tr>`;
      }
      html += '</tbody></table>';
    } else {
      html += '<div style="padding:6px;color:#999;font-style:italic">Itens nao disponiveis</div>';
    }
    html += '</div>';
  });

  // Total so na ultima pagina (ou unica)
  const mostrarTotal = !p.pagina || p.pagina.mostrarTotal;
  if (mostrarTotal) {
    html += `<div style="border-top:2px solid #000;padding-top:8px;margin-top:12px;font-weight:bold;font-size:16px;display:flex;justify-content:space-between"><span>TOTAL</span><span>R$ ${fmt(p.totalSelecionado)}</span></div>`;
  }
  html += `<div style="text-align:center;margin-top:24px;font-size:11px;color:#999">Fio de Cobre by ControleFC</div>
</body></html>`;
  return html;
}

// Impressao e carga de imagem vivem na peca compartilhada `utils/html-para-jpg` desde que o
// extrato da Venda CM precisou da mesma saida. Reexportado com o nome antigo pra nao mexer em
// quem ja importa daqui (ContasReceberLista, ConciliacaoBancariaTab).
export { imprimirHtml as imprimirHTML } from '../../../utils/html-para-jpg';
const loadImgAsDataUrl = carregarImagemComoDataUrl;

// Limite suave de linhas por pagina pro JPG nao ficar gigante e ilegivel no WhatsApp.
// Cada item da venda conta 1 linha; cada bloco (cabecalho da venda) conta 2.
const MAX_LINHAS_POR_PAGINA = 90;

// Pagina os contas em grupos respeitando o limite de linhas.
function paginar(contas: ContaParaExtrato[], extratoData: ExtratoData): ContaParaExtrato[][] {
  const paginas: ContaParaExtrato[][] = [];
  let atual: ContaParaExtrato[] = [];
  let linhas = 0;
  for (const c of contas) {
    const qtdLinhas = ((extratoData[c.id]?.itens || []).length || 1) + 2; // +2 cabecalho do bloco
    if (atual.length > 0 && linhas + qtdLinhas > MAX_LINHAS_POR_PAGINA) {
      paginas.push(atual);
      atual = [];
      linhas = 0;
    }
    atual.push(c);
    linhas += qtdLinhas;
  }
  if (atual.length > 0) paginas.push(atual);
  return paginas;
}

// Gera JPGs (base64 sem prefixo data:) do extrato pra envio via WhatsApp.
// Pre-carrega fotos como dataURL (CORS-safe), pagina conforme MAX_LINHAS_POR_PAGINA,
// renderiza cada pagina num <div> off-screen, captura com html2canvas.
// Retorna array de base64 (1 elemento se cabe tudo numa pagina).
export async function gerarExtratoJpg(p: ExtratoParams): Promise<string[]> {
  // Ordena contas pela data de emissao
  const vendasOrdenadas = [...p.contas].sort((a, b) => (a.data_emissao || '').localeCompare(b.data_emissao || ''));

  // Coleta URLs de fotos unicas e pre-carrega como dataURL (CORS-safe)
  const fotoUrls = [...new Set(
    vendasOrdenadas.flatMap((c) => (p.extratoData[c.id]?.itens || []).map((i) => i.foto_url).filter((u): u is string => !!u))
  )];
  const fotoMap: Record<string, string> = {};
  await Promise.all(fotoUrls.map(async (u) => {
    const d = await loadImgAsDataUrl(u);
    if (d) fotoMap[u] = d;
  }));

  // Substitui src das fotos pelo dataURL (clone do extratoData com fotos resolvidas)
  const dataResolvida: ExtratoData = {};
  for (const [crId, info] of Object.entries(p.extratoData)) {
    dataResolvida[Number(crId)] = {
      comprador: info.comprador,
      nf_numero: info.nf_numero,
      nf_tipo: info.nf_tipo,
      itens: info.itens.map((it: ItemExtrato) => ({ ...it, foto_url: it.foto_url ? (fotoMap[it.foto_url] ?? null) : null })),
    };
  }

  const paginas = paginar(vendasOrdenadas, dataResolvida);
  const totalPaginas = paginas.length;
  const jpgs: string[] = [];

  for (let i = 0; i < paginas.length; i++) {
    const html = gerarExtratoHTML({
      ...p,
      contas: paginas[i]!,
      extratoData: dataResolvida,
      pagina: { atual: i + 1, total: totalPaginas, mostrarTotal: i === paginas.length - 1 },
    });

    jpgs.push(await htmlParaJpg(html));
  }

  return jpgs;
}
